import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Users, UserCircle, LogOut, AlertTriangle, Building2, KanbanSquare } from "lucide-react";
import { useDashAuth } from "./AuthContext";

const NAV = [
  { to: "/dashboard",                       label: "Overview",           icon: LayoutDashboard, end: true },
  { to: "/dashboard/pipeline",              label: "Pipeline",           icon: KanbanSquare },
  { to: "/dashboard/companies",             label: "Companies",          icon: Building2 },
  { to: "/dashboard/submissions",           label: "Submissions",        icon: Inbox },
  { to: "/dashboard/failed-submissions",    label: "Failed submissions", icon: AlertTriangle },
  { to: "/dashboard/users",                 label: "Team",               icon: Users, adminOnly: true },
  { to: "/dashboard/account",               label: "Account",            icon: UserCircle },
];

export const DashboardLayout = () => {
  const { user, logout } = useDashAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/dashboard/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        {/* Sidebar */}
        <aside
          className="hidden md:flex md:w-60 md:flex-col md:fixed md:inset-y-0 bg-slate-900 text-slate-100"
          data-testid="dashboard-sidebar"
        >
          <div className="px-6 py-5 border-b border-slate-800">
            <div className="font-heading font-bold text-lg tracking-tight">Portico</div>
            <div className="text-xs text-slate-400">Admin Dashboard</div>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1">
            {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`dash-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                      isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {n.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="border-t border-slate-800 p-4">
            <div className="text-xs text-slate-400 mb-1">Signed in as</div>
            <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
            <div className="text-xs text-slate-400 capitalize">{user?.role?.replace("_", " ")}</div>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="dash-logout-btn"
              className="mt-3 inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-slate-900 text-white px-4 py-3">
          <div className="font-heading font-bold">Portico Admin</div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="dash-mobile-logout-btn"
            className="text-xs flex items-center gap-1.5"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>

        {/* Main */}
        <main className="flex-1 md:ml-60">
          {/* Mobile nav strip */}
          <nav className="md:hidden flex gap-1 overflow-x-auto bg-white border-b border-slate-200 px-3 py-2">
            {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `px-3 py-1.5 text-xs rounded-full whitespace-nowrap ${
                    isActive ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
